// Capa de analítica para GA4. Deriva el contexto de negocio (servicio, zona,
// tipo de contenido) directamente de la URL, de modo que TODOS los eventos
// viajan enriquecidos sin instrumentar página por página. Esta base es la que
// permite, a futuro, personalizar la web según el interés real del usuario
// (servicio que repara, zona donde está, nivel de intención).

// Slugs de servicio (deben coincidir con SERVICES en seo-data.ts). Se duplican
// aquí a propósito para no cargar todo seo-data.ts en el bundle del cliente.
export const SERVICE_SLUGS = [
  "calentadores",
  "estufas",
  "hornos",
  "gas",
  "plomeria",
  "locativas",
] as const;

export type PageContext = {
  content_type:
    | "home"
    | "service_hub"
    | "zone_hub"
    | "service_zone"
    | "service_brand"
    | "zones_index"
    | "other";
  service?: string;
  zone?: string;
  brand?: string;
};

/** Clasifica un pathname en el contexto de negocio de la página. */
export function parsePath(pathname: string): PageContext {
  const seg = pathname.split("/").filter(Boolean);
  if (seg.length === 0) return { content_type: "home" };

  if (seg[0] === "zonas") {
    return seg[1]
      ? { content_type: "zone_hub", zone: seg[1] }
      : { content_type: "zones_index" };
  }

  if ((SERVICE_SLUGS as readonly string[]).includes(seg[0])) {
    if (seg[1] === "marca") {
      return { content_type: "service_brand", service: seg[0], brand: seg[2] };
    }
    if (seg[1]) {
      return { content_type: "service_zone", service: seg[0], zone: seg[1] };
    }
    return { content_type: "service_hub", service: seg[0] };
  }

  return { content_type: "other" };
}

/** Mapea el contexto de un destino a un evento de navegación semántico. */
export function navEventFor(
  dest: PageContext
): { name: string; params: Record<string, string> } | null {
  switch (dest.content_type) {
    case "zone_hub":
      return { name: "select_zone", params: { zone: dest.zone ?? "" } };
    case "service_hub":
      return { name: "select_service", params: { service: dest.service ?? "" } };
    case "service_zone":
      return {
        name: "select_service_zone",
        params: { service: dest.service ?? "", zone: dest.zone ?? "" },
      };
    case "service_brand":
      return {
        name: "select_brand",
        params: { service: dest.service ?? "", brand: dest.brand ?? "" },
      };
    case "zones_index":
      return { name: "select_zones_index", params: {} };
    default:
      return null;
  }
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/** Envía un evento a GA4, descartando parámetros vacíos. Silencioso si GA4 no está. */
export function track(name: string, params: Record<string, unknown> = {}): void {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  const clean: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") clean[k] = v;
  }
  window.gtag("event", name, clean);
}
